"use client";

import { motion } from "framer-motion";
import { useRef } from "react";
import { cn } from "@/lib/utils";
import { GradientLine } from "@/app/components/ui/gradient-line";

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
}

const testimonials: Testimonial[] = [
  {
    quote:
      "David & Sinead Griffith & the Panoptic team provide an excellent service. Attention to detail, clear communication and great post sales support. David's vast IT experience is second to none. Highly recommended.",
    author: "Eoin Campbell",
    role: "Technical Staff",
    company: "Dublin City University",
  },
  {
    quote:
      "David and the team have been such a pleasure to work with. They deliver on everything they promise and add great value and direction to all choices being made. We are delighted to have chosen Panoptics as our IT partner to support our business. We would have no hesitation in recommending David and the team.",
    author: "Helen Dooley",
    role: "COO",
    company: "CR Payroll",
  },
  {
    quote:
      "Nothing is too much for the team at Panoptic IT. David is fantastic along with the rest of the team who are all highly knowledgeable, helpful and friendly. Highly recommend!",
    author: "Sam Nicholson",
    role: "Recruitment Manager",
    company: "CSA Consultants",
  },
];

const BackgroundGlow = () => (
  <>
    {/* Top-right glow */}
    <motion.div
      className="absolute top-0 right-0 w-[800px] h-[800px] bg-[rgb(var(--color-neon-blue)_/_0.3)] rounded-full blur-[120px] -translate-y-1/4"
      animate={{
        scale: [1, 1.1, 1],
        opacity: [0.3, 0.4, 0.3],
        x: [0, 20, 0],
        y: [-100, -80, -100],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />

    {/* Bottom-left glow */}
    <motion.div
      className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-[rgb(var(--color-electric-blue)_/_0.3)] rounded-full blur-[120px] translate-y-1/4"
      animate={{
        scale: [1, 1.15, 1],
        opacity: [0.3, 0.4, 0.3],
        x: [0, -30, 0],
        y: [100, 80, 100],
      }}
      transition={{
        duration: 10,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  </>
);

export default function TestimonialsSection() {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <section className="w-full bg-[rgb(var(--color-primary-dark)_/_0.95)] text-white py-32 relative overflow-hidden">
      <BackgroundGlow />

      {/* Grid Pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(to right, rgb(255 255 255 / 0.02) 1px, transparent 1px),
                           linear-gradient(to bottom, rgb(255 255 255 / 0.02) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[rgb(var(--color-primary-dark))] via-transparent to-[rgb(var(--color-primary-dark))]" />

      <div className="container mx-auto px-4 relative">
        {/* Section Header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl font-bold tracking-[-0.02em] mb-6 text-white">
            What Our Customers Are Saying
          </h2>
          <p className="text-lg text-[rgb(var(--color-text-secondary))] max-w-2xl mx-auto tracking-[0.01em] leading-[1.8]">
            Don&apos;t just take our word for it. Here&apos;s what businesses
            like yours have to say about working with us.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="relative h-full bg-[rgb(var(--color-primary-dark)_/_0.5)] backdrop-blur-xl rounded-xl border border-[rgb(var(--color-neon-blue)_/_0.1)] shadow-[0_8px_16px_rgba(0,0,0,0.2),_0_0_20px_rgba(var(--color-neon-blue),0.1)] hover:bg-[rgb(var(--color-primary-dark)_/_0.7)] p-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: index * 0.2 }}
            >
              {/* Quote Icon */}
              <svg
                className="w-10 h-10 text-[rgb(var(--color-neon-blue)_/_0.3)] mb-6"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>

              {/* Quote */}
              <p className="text-base text-[rgb(var(--color-text-secondary))] mb-8 tracking-[0.01em] leading-[1.8]">
                {testimonial.quote}
              </p>

              {/* Author */}
              <div>
                <p className="font-medium text-white">{testimonial.author}</p>
                <p className="text-sm text-[rgb(var(--color-text-secondary))]">
                  {testimonial.role}, {testimonial.company}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Bottom Gradient Line */}
      <div className="absolute bottom-0 right-0 w-2/3">
        <GradientLine />
      </div>
    </section>
  );
}
